import { useEffect, useState, useMemo, useRef } from "react";
import { Dialog } from "primereact/dialog";
import LinkHandler from "./LinkHandler.tsx";
import GoogleSheetsDialog from "./GoogleSheetsDialog.tsx";
import { useFlowStore } from "../../store/FlowStateStore.ts";
import { CanvasNode, CanvasNodeData } from "../../models/CanvasNode.ts";
import { Environment as environment } from "../../environment.ts";
import axios from "axios";

const GoogleSheets: React.FC<any> = (data) => {
    const { nodes, setNodes, businessId } = useFlowStore();
    const [showDialog, setShowDialog] = useState(false);
    const [isConnected, setIsConnected] = useState<boolean | null>(null);
    const [summary, setSummary] = useState<{ operation: string; sheetName: string; arrayAttributeName?: string; rowStart?: number; rowEnd?: number } | null>(null);
    const isLoaded = useRef(false);

    const BRANCHES = useMemo(() => {
        const node = nodes.find((n) => n.data.id === data.nodeId);
        const content = (node?.data as CanvasNodeData)?.content?.find((c) => c.id === data.subNodeId);
        const saved = content?.data as any;
        return [
            { id: saved?.successNode || crypto.randomUUID(), label: "Success", color: "green" },
            { id: saved?.failureNode || crypto.randomUUID(), label: "Failure", color: "tomato" },
        ];
    }, []);

    useEffect(() => {
        const node = nodes.find((n) => n.data.id === data.nodeId);
        const content = (node?.data as CanvasNodeData)?.content?.find((c) => c.id === data.subNodeId);
        const saved = content?.data as any;
        if (saved?.operation) setSummary({ operation: saved.operation, sheetName: saved.sheetName || "", arrayAttributeName: saved.arrayAttributeName || "", rowStart: saved.rowStart, rowEnd: saved.rowEnd });
        isLoaded.current = true;
    }, []);

    useEffect(() => {
        axios.get(`${environment.baseUrl}/google/status?businessId=${businessId}`)
            .then((res) => setIsConnected(res.data.connected))
            .catch(() => setIsConnected(false));
    }, []);

    const operationLabel = (op: string) => ({ APPEND: "Append Row", UPDATE: "Update Row", READ: "Read Row", READ_MULTIPLE: "Read Multiple Rows" }[op] || op);

    return (
        <>
            <div className="node-body">
                {isConnected === false ? (
                    <div style={{ padding: "10px", background: "#fff3cd", borderRadius: "6px", fontSize: "11px", color: "#856404", textAlign: "center" }}>
                        Connect Google in <strong>App Store</strong> first
                    </div>
                ) : (
                    <div>
                        {summary ? (
                            <div style={{ marginBottom: "8px", padding: "8px", background: "#f0fafa", borderRadius: "6px", border: "1px solid #c8e6e8" }}>
                                <div style={{ fontSize: "11px", fontWeight: 600, color: "var(--primary-bg-color)" }}>{operationLabel(summary.operation)}</div>
                                {summary.sheetName && <div style={{ fontSize: "11px", color: "#666", marginTop: "2px" }}>{summary.sheetName}</div>}
                                {summary.operation === "READ_MULTIPLE" && summary.arrayAttributeName && (
                                    <div style={{ fontSize: "11px", color: "var(--primary-bg-color)", marginTop: "2px" }}>
                                        rows {summary.rowStart}–{summary.rowEnd} → <strong>{summary.arrayAttributeName}</strong>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div style={{ fontSize: "11px", color: "#999", marginBottom: "8px" }}>Not configured</div>
                        )}
                        <button onClick={() => setShowDialog(true)} className="font-label add-content">
                            Configure Google Sheets
                        </button>
                    </div>
                )}

                {BRANCHES.map((b) => (
                    <div key={b.id} style={{ position: "relative", border: `1px solid ${b.color}`, width: "100%", backgroundColor: "white", padding: "5px", borderRadius: "5px", marginTop: "5px" }}>
                        <div style={{ textAlign: "center", color: b.color, fontSize: "12px" }}>{b.label}</div>
                        <LinkHandler idx={b.id} />
                    </div>
                ))}
            </div>

            <Dialog
                header="Google Sheets"
                visible={showDialog}
                modal
                appendTo={document.body}
                maskStyle={{ background: "rgba(0,0,0,0.6)", zIndex: 9998 }}
                contentStyle={{ zIndex: 9999 }}
                style={{ width: "600px" }}
                onHide={() => setShowDialog(false)}
            >
                <GoogleSheetsDialog
                    data={data}
                    branches={BRANCHES}
                    onClose={(saved) => {
                        setShowDialog(false);
                        if (saved) setSummary({ operation: saved.operation, sheetName: saved.sheetName, arrayAttributeName: saved.arrayAttributeName, rowStart: saved.rowStart, rowEnd: saved.rowEnd });
                    }}
                />
            </Dialog>
        </>
    );
};

export default GoogleSheets;
